import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Pagination } from '@/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Check, X, ShieldAlert, AlertCircle } from 'lucide-react'
import { usePagination, useSecurityReviewRun, useSecurityReviewFindings, useSetSecurityReviewFindingDecision } from '@/hooks'
import type { SecurityFindingKind, SecurityFindingSeverity, SecurityReviewFinding } from '@/types'

interface Props {
  projectId: string
  workflowRunId: string
  canDecide: boolean
  /** Drops the Type and Location columns so the table fits the narrower chat column (frontend.md's
   *  ~300px free-text-column rule can't be met with all 5 columns at the 1500px app minimum width). */
  compact?: boolean
}

const severityStyles: Record<SecurityFindingSeverity, string> = {
  Blocker: 'bg-danger text-danger-foreground border !border-danger-foreground',
  Critical: 'bg-danger text-danger-foreground border !border-danger-foreground',
  Major: 'bg-warning text-warning-foreground border !border-warning-foreground',
  Minor: 'bg-neutral text-neutral-foreground border !border-neutral-foreground',
  Info: 'bg-neutral text-neutral-foreground border !border-neutral-foreground',
}

function SeverityBadge({ severity }: { severity: SecurityFindingSeverity }) {
  return (
    <Badge variant="secondary" className={severityStyles[severity]}>
      {severity}
    </Badge>
  )
}

function KindBadge({ kind }: { kind: SecurityFindingKind }) {
  return (
    <Badge variant="outline" className="whitespace-nowrap">
      {kind === 'Hotspot' ? 'Hotspot' : 'Vulnerability'}
    </Badge>
  )
}

function FindingRow({
  finding,
  canDecide,
  onDecide,
  isPending,
  compact,
}: {
  finding: SecurityReviewFinding
  canDecide: boolean
  onDecide: (findingId: string, decision: 'Accepted' | 'Rejected') => void
  isPending: boolean
  compact?: boolean
}) {
  const location = finding.line ? `${finding.filePath}:${finding.line}` : finding.filePath
  const isHotspot = finding.kind === 'Hotspot'

  return (
    <TableRow data-testid={`security-finding-row-${finding.id}`}>
      <TableCell><SeverityBadge severity={finding.severity} /></TableCell>
      {!compact && <TableCell><KindBadge kind={finding.kind} /></TableCell>}
      {!compact && <TableCell className="max-w-[220px] truncate font-mono text-xs" title={location}>{location}</TableCell>}
      <TableCell className="max-w-[320px]">
        {compact && (
          <p className="font-mono text-xs text-muted-foreground" title={location}>{location}</p>
        )}
        <p className={cn('text-sm', !compact && 'truncate')} title={finding.message}>{finding.message}</p>
        {finding.suggestedFixDescription ? (
          <p className={cn('text-xs text-muted-foreground', !compact && 'truncate')} title={finding.suggestedFixDescription}>
            Suggested fix: {finding.suggestedFixDescription}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            No fix suggested — {isHotspot ? 'Fix it' : 'Accept'} to have the agent work one out
          </p>
        )}
        {finding.suggestedFixDiff && (
          <details className="mt-1" data-testid={`security-finding-diff-${finding.id}`}>
            <summary className="cursor-pointer text-xs font-medium text-info-foreground">
              View suggested fix diff
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-2 font-mono text-xs">
              {finding.suggestedFixDiff}
            </pre>
          </details>
        )}
      </TableCell>
      <TableCell className="w-[180px]">
        {finding.decision === 'Pending' ? (
          canDecide ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 text-success-foreground"
                disabled={isPending}
                aria-label={isHotspot
                  ? `Fix it: ${finding.rule} at ${location}`
                  : `Accept: ${finding.rule} at ${location}`}
                onClick={() => onDecide(finding.id, 'Accepted')}
              >
                <Check className="h-3.5 w-3.5" /> {isHotspot ? 'Fix it' : 'Accept'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 text-danger-foreground"
                disabled={isPending}
                aria-label={isHotspot
                  ? `Mark safe: ${finding.rule} at ${location}`
                  : `Reject: ${finding.rule} at ${location}`}
                onClick={() => onDecide(finding.id, 'Rejected')}
              >
                <X className="h-3.5 w-3.5" /> {isHotspot ? 'Mark safe' : 'Reject'}
              </Button>
            </div>
          ) : (
            <Badge variant="outline">Pending review</Badge>
          )
        ) : (
          <Badge
            variant="secondary"
            className={finding.decision === 'Accepted'
              ? 'bg-success text-success-foreground border !border-success-foreground'
              : 'bg-danger text-danger-foreground border !border-danger-foreground'}
          >
            {finding.decision === 'Rejected' && isHotspot ? 'Marked safe' : finding.decision}
          </Badge>
        )}
      </TableCell>
    </TableRow>
  )
}

export function SecurityReviewFindingsSection({ projectId, workflowRunId, canDecide, compact }: Props) {
  const { page, pageSize, paginationParams, setPage, setPageSize } = usePagination({ defaultPageSize: 20 })
  const { data: securityReviewRun, isLoading: isRunLoading, isError: isRunError, refetch: refetchRun } = useSecurityReviewRun(projectId, workflowRunId)
  const { data, isLoading: areFindingsLoading, isError: areFindingsError, refetch: refetchFindings } = useSecurityReviewFindings(
    projectId,
    securityReviewRun?.id ?? null,
    paginationParams,
    'severity',
    'desc',
  )
  const setDecision = useSetSecurityReviewFindingDecision(projectId)

  if (isRunLoading) {
    return <Skeleton className="h-32 w-full" />
  }

  if (isRunError) {
    return (
      <div className="rounded-lg border bg-card p-4 space-y-2" data-testid="security-review-error">
        <div className="flex items-center gap-2 font-semibold text-danger">
          <AlertCircle className="h-4 w-4" />
          Security Review Error
        </div>
        <p className="text-sm text-muted-foreground">
          Failed to load the security review. Please try again.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetchRun()}
          aria-label="Retry loading security review"
        >
          Retry
        </Button>
      </div>
    )
  }

  if (!securityReviewRun) {
    return null
  }

  const findings = data?.items ?? []
  const totalCount = data?.totalCount ?? securityReviewRun.totalFindingsCount

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3" data-testid="security-review-findings">
      <div className="flex items-center gap-2 font-semibold">
        <ShieldAlert className="h-4 w-4" />
        Security Review Findings
        <Badge variant="outline">{totalCount}</Badge>
      </div>

      {totalCount === 0 ? (
        <p className="text-sm text-muted-foreground">No vulnerabilities or security hotspots were found in this scan.</p>
      ) : areFindingsLoading ? (
        <div className="space-y-2" role="status" aria-live="polite" aria-label="Loading findings">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : areFindingsError ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 font-semibold text-danger">
            <AlertCircle className="h-4 w-4" />
            Failed to load findings
          </div>
          <p className="text-sm text-muted-foreground">
            The findings could not be loaded. Please try again.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchFindings()}
            aria-label="Retry loading findings"
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table aria-label="Security review findings">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Severity</TableHead>
                {!compact && <TableHead className="w-[120px]">Type</TableHead>}
                {!compact && <TableHead className="w-[220px]">Location</TableHead>}
                <TableHead>Finding</TableHead>
                <TableHead className="w-[180px]">Decision</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {findings.map((finding) => (
                <FindingRow
                  key={finding.id}
                  finding={finding}
                  canDecide={canDecide}
                  isPending={setDecision.isPending && setDecision.variables?.findingId === finding.id}
                  onDecide={(findingId, decision) => setDecision.mutate({ findingId, data: { decision } })}
                  compact={compact}
                />
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  )
}